import AttributeParser from "../utils/AttributeParser";
import { getSharedStyleSheet } from './sharedStyleSheet.js';

class FileBrowserFolder extends HTMLElement {
    static observedAttributes = ['id', 'department-id', 'project-id', 'path', 'title', 'expanded'];

    constructor() {
        super();
        this.browserRoot = null;
    }

    attributeChangedCallback(attrName, oldValue, newValue) {
        this.dynamicRender();
    }

    connectedCallback() {
        if (!this.shadowRoot) {
            this.attachShadow({mode: 'open', delegatesFocus: true});

            // Share a single parsed stylesheet across all shadow roots
            getSharedStyleSheet().then(sheet => {
                this.shadowRoot.adoptedStyleSheets = [sheet];
            });

            this._wrapper = document.createElement('div');
            this._wrapper.className = 'file-browser-item-wrapper';
            this._wrapper.tabIndex = 0;
            this.shadowRoot.appendChild(this._wrapper);

            const folder = `<div class="file-browser-folder"><div class="file-browser-folder-collapse fa-icon-chevron-right"></div>
                <file-thumb size="micro"></file-thumb></div>`;
            this._wrapper.insertAdjacentHTML('beforeend', folder);

            this._item = this._wrapper.querySelector('.file-browser-folder');
            this._collapse = this._wrapper.querySelector('.file-browser-folder-collapse');
            this._thumb = this._wrapper.querySelector('file-thumb');

            const project_id = this.getAttribute('project-id');
            const dept_id = this.getAttribute('department-id') ?? -1;
            const path = this.getAttribute('path') ?? '/';

            const childContainer = `<file-browser-child-container project-id="${project_id}" department-id="${dept_id}" path="${path}" class="file-browser-folder-children"></file-browser-child-container>`;
            this._wrapper.insertAdjacentHTML('beforeend', childContainer);

            this._childContainer = this._wrapper.querySelector('.file-browser-folder-children');
            this._childContainer.setBrowserRoot(this.browserRoot);

            this.setupEvents();
        }

        this.dynamicRender();

        if (!!this.getAttribute('expanded')) {
            this.expand();
        }
    }

    setupEvents() {
        this._wrapper.setAttribute('role', 'group');
        this._wrapper.tabIndex = -1;
        this._item.tabIndex = 0;
        this._item.setAttribute('role', 'button');
        this._item.addEventListener('keydown', event => {
            if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                if (AttributeParser.parseBooleanAttribute(this.getAttribute('selectable') ?? false)) this._item.click();
                else if (this.isExpanded()) this.collapse();
                else this.expand();
            } else if (event.key === 'ArrowRight') {
                event.preventDefault(); this.expand();
            } else if (event.key === 'ArrowLeft') {
                event.preventDefault(); this.collapse();
            }
        });
        this._collapse.addEventListener('click', e => {
            e.stopPropagation();
            if (this.isExpanded()) {
                this.collapse();
            } else {
                this.expand();
            }
        });

        this._item.addEventListener('click', e => {
            if (!AttributeParser.parseBooleanAttribute(this.getAttribute('selectable') ?? false)) return;
            const id = +(this.getAttribute('file-id') ?? -1);
            const dept_id = +(this.getAttribute('department-id') ?? -1);
            const path = this.getAttribute('path') ?? '/';
            this.browserRoot.updateSelection(id, dept_id, path);
        });
    }

    dynamicRender() {
        if (!this._wrapper) return;

        this._item.setAttribute('aria-label', this.getAttribute('file-title') ?? 'Folder');
        this._thumb.setAttribute('file-title', this.getAttribute('file-title') ?? '');

        if (AttributeParser.parseBooleanAttribute(this.getAttribute('is-department') ?? false)) {
            this._thumb.setAttribute('src', '/img/Dept_Folder.svg');
        } else {
            this._thumb.setAttribute('src', '/img/Folder.svg');
        }

        if (AttributeParser.parseBooleanAttribute(this.getAttribute('selectable') ?? false)) {
            this._thumb.classList.add('selectable');
        } else {
            this._thumb.classList.remove('selectable');
        }
    }

    reset() {
        this.collapse();
        this._childContainer.childItems = [];
        this._childContainer.setAttribute('project-id', this.getAttribute('project-id'));
    }

    expand() {
        if (this.isExpanded()) return;

        this.setAttribute('expanded', 'expanded');
        this._item.setAttribute('aria-expanded', 'true');
        this._childContainer.classList.add('expanded');

        this._collapse.classList.add('expanded');
        this._collapse.classList.add('fa-icon-chevron-down');
        this._collapse.classList.remove('fa-icon-chevron-right');

        this._childContainer.setBrowserRoot(this.browserRoot);
        this._childContainer.render();
    }

    collapse() {
        if (!this.isExpanded()) return;

        this.removeAttribute('expanded');
        this._item.setAttribute('aria-expanded', 'false');
        this._childContainer.classList.remove('expanded');

        this._collapse.classList.remove('expanded');
        this._collapse.classList.remove('fa-icon-chevron-down');
        this._collapse.classList.add('fa-icon-chevron-right');
    }

    isExpanded() {
        return !!this.getAttribute('expanded') && this._childContainer.classList.contains('expanded');
    }

    updateSelection(id, dept_id, path) {
        this.setSelected(false);
        if (!!this.getAttribute('is-department') && dept_id > 0 &&
            path === '/' && +this.getAttribute('department-id') === dept_id) {
            this.setSelected(true);
        } else if (id > 0 && +this.getAttribute('file-id') === id) {
            this.setSelected(true);
        } else if (id === -1 && dept_id === -1 && +this.getAttribute('department-id') === dept_id &&
            +this.getAttribute('file-id') === -1 && this.getAttribute('path')  === '/') {
            this.setSelected(true);
        }

        this._childContainer.updateSelection(id, dept_id, path);
    }

    setSelected(selected) {
        this._item.setAttribute('aria-pressed', String(selected));
        if (selected) {
            this._item.classList.add('selected');
        } else {
            this._item.classList.remove('selected');
        }
    }

    setBrowserRoot(root) {
        this.browserRoot = root;
        if (this._childContainer) this._childContainer.setBrowserRoot(root);
    }

    itemWithID(id) {
        if (+this.getAttribute('file-id') === +id) {
            return {
                id: (AttributeParser.parseBooleanAttribute(this.getAttribute('is-department')) ? -1 : +this.getAttribute('file-id')),
                department_id: +this.getAttribute('department-id'),
                path: this.getAttribute('path'),
                project_id: +this.getAttribute('project-id'),
                department_name: this.getDepartmentName(),
                title: this.getAttribute('file-title')
            };
        }

        return this._childContainer.itemWithID(id);
    }

    itemWithPath(path, dept_id) {
        if (+this.getAttribute('department-id') === +dept_id &&
            this.getAttribute('path') === path) {
            return {
                id: (AttributeParser.parseBooleanAttribute(this.getAttribute('is-department')) ? -1 : +this.getAttribute('file-id')),
                department_id: dept_id,
                path: path,
                project_id: this.getAttribute('project-id'),
                department_name: this.getDepartmentName(),
                title: this.getAttribute('file-title')
            };
        }

        return this._childContainer.itemWithPath(path, dept_id);
    }

    getDepartmentName() {
        if (AttributeParser.parseBooleanAttribute(this.getAttribute('is-department'))) {
            return this.getAttribute('file-title');
        }

        if (+this.getAttribute('department-id') < 1) return '(No Department)';

        const dept = this.browserRoot.itemWithPath('/', +this.getAttribute('department-id'));

        if (dept) {
            return dept.title;
        }

        return null;
    }
}

customElements.define('file-browser-folder', FileBrowserFolder);

